package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.AppDialogs
import org.example.project.enginetabular.TabularSideSplitScreen
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled

@Composable
internal fun FlowProfileSettings(node: String, initialProfileId: String = "", onClose: () -> Unit, rollenKey: String = "LinkedInFlow") {
    val scope = rememberCoroutineScope()
    var profiles by remember { mutableStateOf<List<All>>(emptyList()) }
    var scripts by remember { mutableStateOf<List<All>>(emptyList()) }
    var appointmentScripts by remember { mutableStateOf<List<All>>(emptyList()) }
    var messages by remember { mutableStateOf<List<All>>(emptyList()) }
    var types by remember { mutableStateOf<List<VertriebsindexApi.LeadTypEintrag>>(emptyList()) }
    var selection by remember { mutableStateOf<String?>(null) }
    var draft by remember { mutableStateOf(All()) }
    var busy by remember { mutableStateOf(true) }
    var popup by remember { mutableStateOf("") }
    var error by remember { mutableStateOf("") }
    val dirty = draft != (profiles.firstOrNull { it.id == draft.id } ?: All())

    fun change(action: () -> Unit) {
        if (dirty) AppDialogs.confirm("Ungespeicherte Änderungen verwerfen?", onConfirm = action) else action()
    }
    LaunchedEffect(Unit) {
        try {
            profiles = FlowProfileApi.load()
            scripts = VertriebsindexApi.loadScriptIndex()
            appointmentScripts = VertriebsindexApi.loadScriptIndex("TERMIN")
            messages = LinkedInDmApi.index()
            types = VertriebsindexApi.loadLeadtypen()
            draft = profiles.firstOrNull { it.id == initialProfileId } ?: All()
            selection = draft.id.takeIf { it.isNotBlank() }
        } catch (e: CancellationException) { throw e }
        catch (e: Exception) { error = e.message ?: "Flow-Profile konnten nicht geladen werden." }
        finally { busy = false }
    }
    Div({ style { height(100.percent); minHeight(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        Div({ style { padding(12.px); flexShrink(0); overflowX("auto") } }) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(16.px); minWidth(100.percent); property("width", "max-content") } }) {
                Button(attrs = {
                    attr("aria-label", "Zurück zum Flow"); if (busy) attr("disabled", "")
                    onClick { change(onClose) }
                    style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); height(42.px); padding(0.px); border(0.px); backgroundColor(Color.transparent); color(UISettings.STATE_BUTTON_TEXT); cursor("pointer"); flexShrink(0) }
                }) {
                    Span({ style { width(36.px); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center) } }) {
                        Img("images/einstellungen.svg", attrs = { attr("alt", ""); style { width(21.px); height(21.px); property("filter", UISettings.ICON_FILTER_ACCENT) } })
                    }
                    Text("Zurück")
                }
                Div({ style { width(170.px); flexShrink(0) } }) {
                    AppDropdown(options = listOf(All(key = "scripts", label = "Call Script"), All(key = "messages", label = "DM Script"), All(key = "appointment-scripts", label = "Termin Script")),
                        selectedValue = popup.takeIf { it in setOf("scripts", "messages", "appointment-scripts") }, onChange = { popup = it },
                        placeholder = "Flow Setup", enabled = !busy, appearance = AppDropdownAppearance.Field,
                        menuMode = AppDropdownMenuMode.Overlay, attrs = { attr("aria-label", "Call Script, DM Script oder Termin Script öffnen") })
                }
                Div({ style { width(190.px); flexShrink(0) } }) {
                    AppDropdown(options = listOf(All(key = "kpi-labels", label = "KPI-Bezeichnungen"), All(key = "followup-deadlines", label = "Follow-up-Fristen")),
                        selectedValue = popup.takeIf { it in setOf("kpi-labels", "followup-deadlines") }, onChange = { popup = it },
                        placeholder = "Flow-Einstellungen", enabled = !busy, appearance = AppDropdownAppearance.Field,
                        menuMode = AppDropdownMenuMode.Overlay, attrs = { attr("aria-label", "Flow-Einstellungen") })
                }
                Div({ style { width(220.px); flexShrink(0) } }) {
                    AppDropdown(
                        options = listOf(All(key = "__new__", label = "Neues Profil")) + profiles.sortedBy { it.vertriebCallName }.map { All(key = it.id, label = it.vertriebCallName) },
                        selectedValue = selection,
                        onChange = { id -> change { selection = id; draft = profiles.firstOrNull { it.id == id } ?: All(); error = "" } },
                        placeholder = "Profil-Auswahl", enabled = !busy,
                        appearance = AppDropdownAppearance.Field, menuMode = AppDropdownMenuMode.Overlay,
                        attrs = { attr("aria-label", "Flow-Profil auswählen oder anlegen") }
                    )
                }
                Div({ style { width(220.px); display(DisplayStyle.Flex); flexShrink(0) } }) {
                    Input(InputType.Text, attrs = {
                        value(draft.vertriebCallName); attr("aria-label", "Profilname")
                        if (busy) disabled()
                        onInput { if (!busy) draft = draft.copy(vertriebCallName = it.value) }
                        style { width(100.percent); height(38.px); minWidth(0.px); padding(4.px, 0.px); border(0.px); backgroundColor(Color.transparent); color(UISettings.STATE_BUTTON_TEXT); boxSizing("border-box"); fontSize(14.px) }
                    })
                }
                Div({ style { width(170.px); flexShrink(0) } }) {
                    AppDropdown(options = listOf(All(key = "CALL", label = "Call Script"), All(key = "TERMIN", label = "Termin Script")),
                        selectedValue = draft.vertriebScriptBereich,
                        onChange = { draft = draft.copy(vertriebScriptBereich = it) },
                        placeholder = "Script verwenden", enabled = !busy && draft.vertriebCallFlow.isEmpty(),
                        appearance = AppDropdownAppearance.Field, menuMode = AppDropdownMenuMode.Overlay,
                        attrs = { attr("aria-label", "Script-Tabelle für dieses Profil") })
                }
                LumaticaButton(if (busy) "Speichert …" else "Speichern", {
                    if (!busy) {
                        val value = draft
                        busy = true; error = ""
                        scope.launch {
                            try {
                                val saved = FlowProfileApi.save(value)
                                profiles = profiles.filterNot { it.id == saved.id } + saved
                                draft = saved; selection = saved.id; LinkedInSyncState.version++
                            } catch (e: CancellationException) { throw e }
                            catch (e: Exception) { error = e.message ?: "Flow-Profil konnte nicht gespeichert werden." }
                            finally { busy = false }
                        }
                    }
                }, enabled = !busy && draft.vertriebCallName.isNotBlank(), variant = LumaticaButtonVariant.Action)
                LumaticaButton("Löschen", {
                    val target = draft
                    if (!busy && target.id.isNotBlank()) AppDialogs.confirm("Flow-Profil „${target.vertriebCallName}“ wirklich löschen?", onConfirm = {
                        if (!busy) {
                            busy = true; error = ""
                            scope.launch {
                                try {
                                    profiles = FlowProfileApi.delete(target.id)
                                    if (draft.id == target.id) { draft = All(); selection = null }
                                    LinkedInSyncState.version++
                                } catch (e: CancellationException) { throw e }
                                catch (e: Exception) { error = e.message ?: "Flow-Profil konnte nicht gelöscht werden." }
                                finally { busy = false }
                            }
                        }
                    })
                }, enabled = !busy && draft.id.isNotBlank(), variant = LumaticaButtonVariant.State)
            }
        }
        if (error.isNotBlank()) Div({ attr("role", "alert"); style { padding(8.px, 24.px); color(UISettings.ACCENT) } }) { Text(error) }
        Div({ style { height(1.px); flexShrink(0); backgroundColor(Color("rgba(255,255,255,.14)")) } })
        Div({ style { flex("1"); minHeight(0.px) } }) {
            key(draft.id) {
                TabularSideSplitScreen(storageId = "flow-profile-editor-$node", initialRatio = .66f, minRatio = .3f, maxRatio = .85f,
                    screen1 = {
                        AkquiseFlowEditor(draft.vertriebCallFlow, emptyList(), if (draft.vertriebScriptBereich == "TERMIN") appointmentScripts else scripts,
                            { if (!busy) draft = draft.copy(vertriebCallFlow = it) }, {}, showCheckboxes = false, minimal = true)
                    },
                    screen2 = {
                        LinkedInDmProfileEditor(draft.vertriebCallCheckboxen,
                            onChange = { if (!busy) draft = draft.copy(vertriebCallCheckboxen = it) }, minimal = true)
                    }
                )
            }
        }
    }
    when (popup) {
        "kpi-labels", "followup-deadlines" -> key(popup) { FlowSettings(popup) { popup = "" } }
        "scripts" -> ScriptIndexDialog(scripts, { scripts = it }, { popup = "" }, rollenKey = rollenKey)
        "appointment-scripts" -> ScriptIndexDialog(appointmentScripts, { appointmentScripts = it }, { popup = "" }, rollenKey = rollenKey, bereich = "TERMIN")
        "messages" -> LinkedInDmIndex(messages, types, { messages = it }, { types = it }, { popup = "" })
    }
}
